import React from 'react';
import PropTypes from 'prop-types';
import Helmet from 'react-helmet';
import { withStyles } from '../tools/withStyles';
import { Main } from '../components/Main';
import { useLanguage } from '../tools/useLanguage';

const styles = () => ({
  root: {
    padding: [0, 0, 80, 0]
  }
});

const defaultDevelopment = {
  metaTitle: 'Development - Texas Fortress AI',
  metaDescription: 'Building tomorrow’s solutions today — secure, production-ready software from AI to infrastructure.',
  heroTitle: 'Building Tomorrow\'s Solutions Today',
  heroSubtitle: '25 years of engineering excellence across every software frontier',
  resumeTitle: 'Our Proven Battle Record',
  resumeParagraphs: [
    'We didn\'t wake up yesterday and decide to build software. We earned our stripes across 25 years of real engineering wars—shipping systems other teams wouldn\'t touch, solving problems bigger shops walked away from, and delivering production platforms before "startup culture" even learned to spell MVP.',
    'This isn\'t a list of what we sell today. This is our resume of impossible things—the proof behind every promise we make now.'
  ],
  buildsTitle: 'What We\'ve Built When It Mattered Most',
  builds: [
    {
      title: 'Financial Systems That Don\'t Flinch',
      items: [
        'Bank-grade payment engines',
        'Cryptocurrency and digital asset platforms',
        'Bitcoin trading systems before they were cool',
        'Real-time transaction networks hardened for abuse, scale, and audits'
      ]
    },
    {
      title: 'Healthcare Platforms That Carried Clinics',
      items: [
        'End-to-end EHR infrastructures',
        'Multi-clinic, multi-state provider networks',
        'Charting, billing, scheduling, and PHI-secure everything',
        'Enterprise practice management built for real clinics'
      ]
    },
    {
      title: 'Enterprise & Government Systems With No Room for Failure',
      items: [
        'Bond and grant management platforms',
        'Construction program oversight systems',
        'District-wide project and financial tracking',
        'Custom CRM, workflow, and operational automation'
      ]
    },
    {
      title: 'Encrypted Communications Before Privacy Was Trendy',
      items: [
        'Encrypted messaging platforms',
        'PGP-driven secure mail systems',
        'Private organizational communication networks'
      ]
    },
    {
      title: 'Emerging Tech Before It Emerged',
      items: [
        'AI and machine-learning integrations',
        'Autonomous and IoT device platforms',
        'Real-time distributed architectures',
        'High-performance systems for edge and hardware-adjacent tech'
      ]
    }
  ],
  whyTitle: 'Why This Matters Today',
  whyBullets: [
    'We\'re not coasting on nostalgia—we\'re showing the receipts.',
    'Every app, automation, interface, and system we build today is backed by a quarter-century of engineering scars, wins, pivots, late-night saves, and production victories.',
    'You\'re not hiring a dev shop. You\'re bringing in a boutique engineering strike team with experience that can\'t be faked.'
  ],
  cta: 'Ready to build something extraordinary? Start a chat with us.'
};

const DevelopmentPage = ({ classes }) => {
  const { t } = useLanguage() || {};
  const content = t ? t('development') : {};
  const metaTitle = content.metaTitle || defaultDevelopment.metaTitle;
  const metaDescription = content.metaDescription || defaultDevelopment.metaDescription;
  const heroTitle = content.heroTitle || defaultDevelopment.heroTitle;
  const heroSubtitle = content.heroSubtitle || defaultDevelopment.heroSubtitle;
  const resumeTitle = content.resumeTitle || defaultDevelopment.resumeTitle;
  const resumeParagraphs = content.resumeParagraphs || defaultDevelopment.resumeParagraphs;
  const buildsTitle = content.buildsTitle || defaultDevelopment.buildsTitle;
  const builds = content.builds || defaultDevelopment.builds;
  const whyTitle = content.whyTitle || defaultDevelopment.whyTitle;
  const whyBullets = content.whyBullets || defaultDevelopment.whyBullets;
  const ctaText = content.cta || defaultDevelopment.cta;
  const requestFitCallUrl = 'https://cal.com/your-handle/fit-call';

  const handleRequestFitCall = event => {
    const bridge = typeof window !== 'undefined' ? window.tfaiDeskBridge : null;
    if (bridge && typeof bridge.requestFitCall === 'function') {
      event.preventDefault();
      bridge.requestFitCall({ fallback: requestFitCallUrl });
    }
  };

  const handleFocusChat = event => {
    event.preventDefault();
    const bridge = typeof window !== 'undefined' ? window.tfaiDeskBridge : null;
    if (bridge && typeof bridge.focusInput === 'function') {
      bridge.focusInput();
      if (bridge.focusLoop) bridge.focusLoop();
    } else {
      if (typeof window !== 'undefined') {
        window.__TFAI_PENDING_FOCUS = true;
        const pollFocus = () => {
          const b = window.tfaiDeskBridge;
          if (b && typeof b.focusInput === 'function') {
            b.focusInput();
            if (b.focusLoop) b.focusLoop();
            window.__TFAI_PENDING_FOCUS = false;
            return true;
          }
          return false;
        };
        if (!pollFocus()) {
          const interval = setInterval(() => {
            if (pollFocus()) clearInterval(interval);
          }, 250);
          setTimeout(() => clearInterval(interval), 5000);
        }
      }
      handleRequestFitCall(event);
    }
  };

  return (
    <Main className={classes.root} flushLeft>
      <Helmet>
        <title>{metaTitle}</title>
        <meta name='description' content={metaDescription} />
        <link rel='preconnect' href='https://fonts.googleapis.com' />
        <link rel='preconnect' href='https://fonts.gstatic.com' crossOrigin='anonymous' />
        <link href='https://fonts.googleapis.com/css2?family=Inter:wght@400;500;600;700;800&display=swap' rel='stylesheet' />
        <script src='https://cdn.tailwindcss.com' />
        <style>{`
          .dev-page {
            position: relative;
            min-height: 100vh;
            overflow-x: hidden;
            color: #ffffff;
            background: linear-gradient(180deg, #0a1628 0%, #1a2f4f 30%, #2d4a7c 60%, #4a6fa5 100%);
            font-family: 'Inter', 'Arial', sans-serif;
          }
          .dev-page::before {
            content: '';
            position: fixed;
            top: 0;
            left: 0;
            width: 100%;
            height: 100%;
            background:
              radial-gradient(ellipse at 20% 30%, rgba(138, 43, 226, 0.15) 0%, transparent 50%),
              radial-gradient(ellipse at 80% 70%, rgba(255, 140, 0, 0.1) 0%, transparent 50%),
              radial-gradient(ellipse at 50% 50%, rgba(100, 149, 237, 0.08) 0%, transparent 60%);
            z-index: -1;
            pointer-events: none;
            animation: cosmicPulse 15s ease-in-out infinite;
          }
          @keyframes cosmicPulse {
            0%, 100% { opacity: 0.6; }
            50% { opacity: 0.9; }
          }
          .dev-shell {
            position: relative;
            z-index: 1;
          }
          .dev-hero {
            min-height: 24vh;
            display: flex;
            flex-direction: column;
            justify-content: center;
            align-items: center;
            text-align: center;
            padding: 24px 12px 12px;
            position: relative;
            background: radial-gradient(ellipse at center, rgba(74, 111, 165, 0.35) 0%, transparent 70%);
          }
          .dev-hero h1 {
            font-size: 64px;
            font-weight: 800;
            letter-spacing: 4px;
            margin-bottom: 20px;
            text-transform: uppercase;
            background: linear-gradient(135deg, #ffffff 0%, #FFD700 100%);
            -webkit-background-clip: text;
            -webkit-text-fill-color: transparent;
            background-clip: text;
            animation: titleGlow 3s ease-in-out infinite;
          }
          @keyframes titleGlow {
            0%, 100% { filter: brightness(1); }
            50% { filter: brightness(1.3); }
          }
          .dev-hero h2 {
            font-size: 24px;
            font-weight: 500;
            letter-spacing: 2px;
            color: #b8c5d6;
            max-width: 800px;
          }
          .dev-container {
            max-width: 1200px;
            margin: 0 auto;
            padding: 24px 12px 48px;
          }
          .dev-section {
            margin-bottom: 56px;
            opacity: 0;
            transform: translateY(30px);
            animation: fadeInUp 0.8s ease forwards;
            animation-delay: var(--delay, 0s);
          }
          @keyframes fadeInUp {
            to {
              opacity: 1;
              transform: translateY(0);
            }
          }
          .dev-section-title {
            font-size: 42px;
            font-weight: 800;
            margin-bottom: 30px;
            text-align: center;
            letter-spacing: 3px;
            color: #FFD700;
            text-transform: uppercase;
          }
          .dev-section-intro {
            font-size: 20px;
            line-height: 1.8;
            color: #d4dce6;
            text-align: center;
            max-width: 900px;
            margin: 0 auto 28px;
          }
          .dev-services-grid {
            display: grid;
            grid-template-columns: repeat(auto-fit, minmax(280px, 1fr));
            gap: 30px;
            margin-top: 50px;
          }
          .dev-service-card {
            background: rgba(26, 47, 79, 0.6);
            border: 1px solid rgba(255, 215, 0, 0.2);
            border-radius: 15px;
            padding: 32px 28px;
            transition: all 0.4s ease;
            position: relative;
            overflow: hidden;
          }
          .dev-service-card::before {
            content: '';
            position: absolute;
            top: 0;
            left: 0;
            width: 100%;
            height: 100%;
            background: linear-gradient(135deg, rgba(255, 215, 0, 0.1) 0%, transparent 100%);
            opacity: 0;
            transition: opacity 0.4s ease;
          }
          .dev-service-card:hover {
            transform: translateY(-10px);
            border-color: rgba(255, 215, 0, 0.6);
            box-shadow: 0 20px 40px rgba(255, 165, 0, 0.2);
          }
          .dev-service-card:hover::before {
            opacity: 1;
          }
          .dev-service-card h3 {
            font-size: 22px;
            color: #FFD700;
            margin-bottom: 15px;
            letter-spacing: 1px;
            position: relative;
            z-index: 1;
          }
          .dev-service-card ul {
            list-style: none;
            margin-top: 12px;
            position: relative;
            z-index: 1;
            padding: 0;
          }
          .dev-service-card ul li {
            font-size: 15px;
            color: #d4dce6;
            padding: 8px 0 8px 25px;
            position: relative;
          }
          .dev-service-card ul li::before {
            content: '▸';
            position: absolute;
            left: 0;
            color: #FFA500;
            font-size: 18px;
          }
          .dev-why {
            background: rgba(26, 47, 79, 0.4);
            border-radius: 20px;
            padding: 60px 32px;
            border: 1px solid rgba(255, 215, 0, 0.3);
          }
          .dev-why ul {
            list-style: none;
            display: grid;
            grid-template-columns: repeat(auto-fit, minmax(250px, 1fr));
            gap: 22px;
            margin-top: 34px;
            padding: 0;
          }
          .dev-why ul li {
            font-size: 17px;
            color: #d4dce6;
            padding: 18px;
            background: rgba(74, 111, 165, 0.3);
            border-radius: 10px;
            border-left: 4px solid #FFD700;
            transition: all 0.3s ease;
          }
          .dev-why ul li:hover {
            background: rgba(74, 111, 165, 0.5);
            transform: translateX(10px);
          }
          .dev-cta {
            text-align: center;
            padding: 80px 20px;
            background: radial-gradient(ellipse at center, rgba(255, 140, 0, 0.15) 0%, transparent 70%);
          }
          .dev-cta-button {
            display: inline-block;
            padding: 18px 48px;
            font-size: 18px;
            font-weight: 800;
            letter-spacing: 2px;
            color: #0a1628;
            background: linear-gradient(135deg, #FFD700 0%, #FFA500 100%);
            border: none;
            border-radius: 50px;
            text-decoration: none;
            text-transform: uppercase;
            transition: all 0.4s ease;
            box-shadow: 0 10px 30px rgba(255, 165, 0, 0.3);
            cursor: pointer;
          }
          .dev-cta-button:hover {
            transform: translateY(-5px) scale(1.05);
            box-shadow: 0 15px 40px rgba(255, 165, 0, 0.5);
          }
          .dev-footer {
            background: rgba(10, 22, 40, 0.95);
            padding: 40px 20px;
            text-align: center;
            border-top: 1px solid rgba(255, 215, 0, 0.3);
          }
          .dev-footer p {
            color: #b8c5d6;
            font-size: 14px;
            letter-spacing: 1px;
          }
          @media (max-width: 900px) {
            .dev-hero {
              min-height: 60vh;
              height: auto;
              padding-top: 64px;
            }
          }
          @media (max-width: 768px) {
            .dev-hero h1 {
              font-size: 42px;
            }
            .dev-hero h2 {
              font-size: 18px;
            }
            .dev-section-title {
              font-size: 32px;
            }
          }
        `}</style>
      </Helmet>

      <div className='dev-page'>
        <div className='dev-shell'>
          <section className='dev-hero'>
            <h1>{heroTitle}</h1>
            <h2>{heroSubtitle}</h2>
          </section>

          <div className='dev-container'>
            <section className='dev-section' style={{ '--delay': '0.2s' }}>
              <h2 className='dev-section-title'>{resumeTitle}</h2>
              {resumeParagraphs.map((paragraph, idx) => (
                <p key={idx} className='dev-section-intro'>
                  {paragraph}
                </p>
              ))}
            </section>

            <section className='dev-section' style={{ '--delay': '0.35s' }}>
              <h2 className='dev-section-title'>{buildsTitle}</h2>
              <div className='dev-services-grid'>
                {builds.map(service => (
                  <div key={service.title} className='dev-service-card'>
                    <h3>{service.title}</h3>
                    <ul>
                      {service.items.map(item => (
                        <li key={item}>{item}</li>
                      ))}
                    </ul>
                  </div>
                ))}
              </div>
            </section>

            <section className='dev-section' style={{ '--delay': '0.5s' }}>
              <div className='dev-why'>
                <h2 className='dev-section-title'>{whyTitle}</h2>
                <ul>
                  {whyBullets.map(item => (
                    <li key={item}>{item}</li>
                  ))}
                </ul>
              </div>
            </section>
          </div>

          <section className='dev-cta' id='contact'>
            <button
              type='button'
              className='dev-cta-button'
              onClick={handleFocusChat}
            >
              {ctaText}
            </button>
          </section>
        </div>
      </div>
    </Main>
  );
};

DevelopmentPage.propTypes = {
  classes: PropTypes.object
};

export default withStyles(styles)(DevelopmentPage);
